import { useCallback, useId, useState } from 'react';
import type * as React from 'react';
import { useCreate, useDelete, useUpdate } from '@refinedev/core';
import { Plus, Trash2 } from 'lucide-react';
import { Page } from '@/components/page';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { SortableList } from '@/components/sortable-list';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogForm,
  DialogBody,
  DialogContent,
  DialogFooter,
} from '@/components/ui/dialog';
import { FormField, Input } from '@/components/ui/field';
import { IconField } from '@/components/icon-field';
import { Badge, Switch, Tooltip } from '@/components/ui/primitives';
import { RESOURCES, type AdminWishlistCategory } from '@/api/resources';
import { useAdminList, useReorder } from '@/hooks/use-admin-resource';
import { ApiError } from '@/api/errors';
import { notify } from '@/lib/notify';
import { formatNumber } from '@/lib/format';

/** Wishlist categories (06 §2) — CRUD and reorder, with the same usage guard. */
export function CategoriesPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminWishlistCategory>(
    RESOURCES.categories,
  );
  const reorder = useReorder(RESOURCES.categories, `${RESOURCES.categories}/reorder`);

  const [editing, setEditing] = useState<AdminWishlistCategory | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminWishlistCategory | null>(null);

  const { mutateAsync: update } = useUpdate();
  const { mutateAsync: remove } = useDelete();

  const toggleActive = useCallback(
    async (category: AdminWishlistCategory) => {
      try {
        await update({
          resource: RESOURCES.categories,
          id: category.id,
          values: { isActive: !category.isActive },
          successNotification: false,
          errorNotification: false,
        });
        notify.success(category.isActive ? 'Deactivated' : 'Activated', category.name);
      } catch (err) {
        notify.error(err, 'The change was not saved');
      } finally {
        invalidate();
      }
    },
    [update, invalidate],
  );

  return (
    <Page
      title="Wishlist categories"
      description="The picker a parent sees when adding a gift idea."
      breadcrumbs={[{ label: 'Master data' }, { label: 'Wishlist categories' }]}
      action={
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Add category
        </Button>
      }
    >
      <div className="overflow-hidden rounded-card border border-line bg-surface">
        {error ? (
          <ErrorState error={error} onRetry={refetch} endpoint="GET /admin/categories" />
        ) : isLoading ? (
          <TableSkeleton columns={3} />
        ) : !rows || rows.length === 0 ? (
          <EmptyState
            title="No categories yet"
            description="Parents cannot file a wishlist item until at least one category exists."
            action={
              <Button variant="primary" onClick={() => setEditing('new')}>
                <Plus aria-hidden="true" />
                Add category
              </Button>
            }
          />
        ) : (
          <SortableList
            items={rows}
            itemKey={(category) => category.id}
            onReorder={(ids) => void reorder(ids)}
            renderItem={(category) => (
              <div className="flex flex-wrap items-center gap-3">
                {/* A category without an icon shows an empty tile, matching
                    interests. Never a broken <img>. */}
                {category.iconUrl ? (
                  <img
                    src={category.iconUrl}
                    alt=""
                    width={28}
                    height={28}
                    className="size-7 shrink-0 rounded object-cover"
                  />
                ) : (
                  <span className="size-7 shrink-0 rounded border border-line bg-surface-sunken" />
                )}

                <button
                  type="button"
                  className="min-w-0 flex-1 truncate rounded text-left text-sm font-medium text-ink"
                  onClick={() => setEditing(category)}
                >
                  {category.name}
                </button>

                <span className="text-xs text-ink-soft" data-numeric>
                  {formatNumber(category.usageCount)} items
                </span>

                {category.isActive ? (
                  <Badge tone="success">Active</Badge>
                ) : (
                  <Badge tone="neutral">Inactive</Badge>
                )}

                <Switch
                  checked={category.isActive}
                  onCheckedChange={() => void toggleActive(category)}
                  aria-label={`${category.isActive ? 'Deactivate' : 'Activate'} ${category.name}`}
                />

                <Tooltip
                  label={
                    category.usageCount > 0
                      ? 'In use by existing wishlists — deactivate instead'
                      : 'Delete'
                  }
                >
                  <span>
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={category.usageCount > 0}
                      aria-label={`Delete ${category.name}`}
                      onClick={() => setDeleting(category)}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </span>
                </Tooltip>
              </div>
            )}
          />
        )}
      </div>

      {editing ? (
        <CategoryDialog
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete ${deleting?.name ?? ''}?`}
        description="This removes the category entirely. It cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove({
              resource: RESOURCES.categories,
              id: deleting.id,
              successNotification: false,
              errorNotification: false,
            });
            notify.success('Deleted', deleting.name);
            setDeleting(null);
          } catch (err) {
            notify.error(err, 'The category was not deleted');
          } finally {
            invalidate();
          }
        }}
      />
    </Page>
  );
}

function CategoryDialog({
  category,
  onClose,
  onSaved,
}: {
  category: AdminWishlistCategory | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(category?.name ?? '');
  // undefined = untouched. Sent only when the admin actually changed the icon.
  const [iconKey, setIconKey] = useState<string | null | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const nameId = useId();

  const { mutateAsync: create } = useCreate();
  const { mutateAsync: update } = useUpdate();

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setFieldErrors({});
    try {
      if (category) {
        await update({
          resource: RESOURCES.categories,
          id: category.id,
          // `iconKey` only when it changed: omitted leaves the existing icon,
          // null clears it.
          values: { name: name.trim(), ...(iconKey !== undefined ? { iconKey } : {}) },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Saved', name.trim());
      } else {
        await create({
          resource: RESOURCES.categories,
          values: { name: name.trim(), ...(iconKey !== undefined ? { iconKey } : {}) },
          successNotification: false,
          errorNotification: false,
        });
        notify.success('Added', name.trim());
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      notify.error(err, 'The category was not saved');
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent title={category ? 'Edit category' : 'Add category'}>
        <DialogForm
          onSubmit={(event) => {
            void submit(event);
          }}
          noValidate
        >
          <DialogBody>
            <FormField label="Name" htmlFor={nameId} error={fieldErrors['name']} required>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={60}
                autoFocus
                required
              />
            </FormField>

            <IconField
              onChange={setIconKey}
              initialPreviewUrl={category?.iconUrl ?? null}
              error={fieldErrors['iconKey']}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={pending} disabled={!name.trim()}>
              {category ? 'Save changes' : 'Add category'}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
